import {useMemo, useState} from 'react'
import {
  Box,
  Typography,
  Stack,
  Autocomplete,
  TextField,
  FormControl,
  MenuItem,
  Select,
  InputLabel,
  Chip,
} from '@mui/material'
import {styled} from '@mui/system'
import MaterialTable from '../../components/MaterialTable'
import {VerticalBarChart} from '../../components/charts/BarChart'
import {
  SummaryCard,
  SummaryCardValue,
  SummaryCardLabel,
  SummaryCardSubtext,
  ChartCard,
  FilterBar,
  RankCircle,
} from './PerformanceStyled'
import {SimplePerformanceCell} from './PerformanceCell'
import {PerformanceLegend} from './PerformanceLegend'
import {formatNumber, numberPrecision} from '../../helpers/UtilityHelper'

const SectionRow = styled(Box)(({theme}) => ({
  display: 'flex',
  gap: theme.spacing(2),
  padding: theme.spacing(2.5, 3),
  alignItems: 'stretch',
}))

const ChartTitle = styled(Typography)({
  fontWeight: 700,
  fontSize: '0.88rem',
  color: '#1a2b3c',
})

const ChartSubtitle = styled(Typography)({
  fontSize: '0.7rem',
  color: '#637381',
  marginTop: 2,
})

const EmptyChart = styled(Typography)({
  padding: '48px 0',
  textAlign: 'center',
  color: '#9ca3af',
  fontSize: '0.82rem',
})

const matchesVolumeRange = (volume, range) => {
  if (range === 'all') return true
  if (range === '<1000') return volume < 1000
  if (range === '>1000') return volume >= 1000
  if (range === '>5000') return volume >= 5000
  if (range === '>20000') return volume >= 20000
  if (range === '>50000') return volume >= 50000
  if (range === '>100000') return volume >= 100000
  return true
}

const volumeRangeOptions = [
  {value: 'all', label: 'All'},
  {value: '<1000', label: '< 1,000'},
  {value: '>1000', label: '> 1,000'},
  {value: '>5000', label: '> 5,000'},
  {value: '>20000', label: '> 20,000'},
  {value: '>50000', label: '> 50,000'},
  {value: '>100000', label: '> 1 Lakh'},
]

export function OverallReverse({data}) {
  const [volumeRange, setVolumeRange] = useState('all')
  const [selectedCourierNames, setSelectedCourierNames] = useState(null)

  const filteredData = useMemo(() => {
    if (!data) return []
    let filtered = [...data]
    if (volumeRange !== 'all') {
      filtered = filtered.filter((item) =>
        matchesVolumeRange(item.totalRVPRequestCount || 0, volumeRange)
      )
    }
    return filtered
  }, [data, volumeRange])

  const sortedData = useMemo(() => {
    const sorted = [...filteredData]
    sorted.sort((a, b) => (b.rvpPickupAdherence || 0) - (a.rvpPickupAdherence || 0))
    return sorted.filter(
      (item) => item.courierId && (selectedCourierNames ? item.name === selectedCourierNames : true)
    )
  }, [filteredData, selectedCourierNames])

  const summaryData = useMemo(() => {
    const valid = sortedData.filter((d) => d.courierId)
    if (valid.length === 0) return {avgRvpTat: 0, best: null, worst: null}
    const totalReq = valid.reduce((s, d) => s + (d.totalRVPRequestCount || 0), 0)
    const avgRvpTat = totalReq > 0
      ? valid.reduce((s, d) => s + (d.rvpPickupAdherence || 0) * (d.totalRVPRequestCount || 0), 0) / totalReq
      : 0
    const best = valid.reduce((b, d) => ((d.rvpPickupAdherence || 0) > (b?.rvpPickupAdherence || 0) ? d : b), valid[0])
    const worst = valid.reduce((w, d) => ((d.rvpPickupAdherence || 0) < (w?.rvpPickupAdherence || 0) ? d : w), valid[0])
    return {avgRvpTat, best, worst}
  }, [sortedData])

  const top10ChartData = useMemo(() => {
    return sortedData.filter((d) => d.courierId).slice(0, 10).map((d) => ({
      name: d.name,
      adherence: numberPrecision(d.rvpPickupAdherence || 0, 1),
    }))
  }, [sortedData])

  const courierNameOptions = useMemo(() => {
    if (!data) return []
    return data.map((item) => ({value: item.name, label: item.name}))
  }, [data])

  const columns = useMemo(
    () => [
      {
        value: (row) => {
          const rank = sortedData.findIndex((item) => item.name === row.name) + 1
          return <RankCircle rank={rank}>{rank}</RankCircle>
        },
        label: '#',
        align: 'center',
      },
      {
        value: (row) => (
          <Box>
            <Typography variant='body2' fontWeight={600} color='text.primary' sx={{whiteSpace: 'normal', lineHeight: 1.3, wordBreak: 'break-word', minWidth: 160}}>{row.name}</Typography>
            <Typography variant='caption' color='text.secondary'>{formatNumber(row.totalRVPRequestCount || 0)} requests</Typography>
          </Box>
        ),
        label: 'Service Type',
        align: 'left',
      },
      {
        value: (row) => <Typography variant='body2' fontWeight={600}>{formatNumber(row.totalRVPRequestCount || 0)}</Typography>,
        label: 'Volume',
        align: 'center',
      },
      {
        value: (row) => <SimplePerformanceCell value={row.rvpPickupAdherence} count={row.pickedupInTATCount} totalCount={row.totalRVPRequestCount} />,
        label: (
          <Stack spacing={0.25} alignItems='center'>
            <Typography variant='caption' fontWeight={700} sx={{fontSize: '0.72rem'}}>RVP TAT</Typography>
            <Typography variant='caption' color='text.secondary' sx={{fontSize: '0.65rem'}}>On Time</Typography>
          </Stack>
        ),
        align: 'center',
        style: () => ({borderLeft: '2px solid #e0e0e0'}),
      },
      {
        value: (row) => <SimplePerformanceCell value={row.rvpPickupPlusOneAdherence} count={row.pickedupInTATPlusOneCount} totalCount={row.totalRVPRequestCount} />,
        label: (
          <Stack spacing={0.25} alignItems='center'>
            <Typography variant='caption' fontWeight={700} sx={{fontSize: '0.72rem'}}>RVP+1</Typography>
            <Typography variant='caption' color='text.secondary' sx={{fontSize: '0.65rem'}}>+1 Day</Typography>
          </Stack>
        ),
        align: 'center',
      },
      {
        value: (row) => <SimplePerformanceCell value={row.rvpPickupPlusTwoAdherence} count={row.pickedupInTATPlusTwoCount} totalCount={row.totalRVPRequestCount} />,
        label: (
          <Stack spacing={0.25} alignItems='center'>
            <Typography variant='caption' fontWeight={700} sx={{fontSize: '0.72rem'}}>RVP+2</Typography>
            <Typography variant='caption' color='text.secondary' sx={{fontSize: '0.65rem'}}>+2 Days</Typography>
          </Stack>
        ),
        align: 'center',
      },
      {
        value: (row) => <SimplePerformanceCell value={row.rvpPickupPlusThreeAdherence} count={row.pickedupInTATPlusThreeCount} totalCount={row.totalRVPRequestCount} />,
        label: (
          <Stack spacing={0.25} alignItems='center'>
            <Typography variant='caption' fontWeight={700} sx={{fontSize: '0.72rem'}}>RVP+3</Typography>
            <Typography variant='caption' color='text.secondary' sx={{fontSize: '0.65rem'}}>+3 Days</Typography>
          </Stack>
        ),
        align: 'center',
      },
    ],
    [sortedData]
  )

  return (
    <Box>
      {/* KPI Cards */}
      <SectionRow>
        <SummaryCard borderColor='#0d9488'>
          <SummaryCardLabel>Avg RVP TAT %</SummaryCardLabel>
          <SummaryCardValue>{numberPrecision(summaryData.avgRvpTat, 1)}%</SummaryCardValue>
          <SummaryCardSubtext>Weighted by volume</SummaryCardSubtext>
        </SummaryCard>
        <SummaryCard borderColor='#f59e0b'>
          <SummaryCardLabel>Best Performer</SummaryCardLabel>
          <SummaryCardValue>{summaryData.best ? numberPrecision(summaryData.best.rvpPickupAdherence, 1) + '%' : '-'}</SummaryCardValue>
          <SummaryCardSubtext>{summaryData.best?.name || '-'}</SummaryCardSubtext>
        </SummaryCard>
        <SummaryCard borderColor='#ef4444'>
          <SummaryCardLabel>Worst Performer</SummaryCardLabel>
          <SummaryCardValue>{summaryData.worst ? numberPrecision(summaryData.worst.rvpPickupAdherence, 1) + '%' : '-'}</SummaryCardValue>
          <SummaryCardSubtext>{summaryData.worst?.name || '-'}</SummaryCardSubtext>
        </SummaryCard>
      </SectionRow>

      {/* Chart */}
      <Box sx={{px: 3, pb: 2.5}}>
        <ChartCard>
          <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2}}>
            <Box>
              <ChartTitle>Top 10 Service Types — RVP TAT Adherence %</ChartTitle>
              <ChartSubtitle>Ranked by on-time reverse pickup performance</ChartSubtitle>
            </Box>
            <Chip label={`${top10ChartData.length} service types`} size='small' variant='outlined' sx={{fontSize: '0.68rem', height: 22}} />
          </Box>
          {top10ChartData.length > 0 ? (
            <VerticalBarChart
              data={top10ChartData}
              height={340}
              colors={null}
              tooltipContent={null}
              chartOptions={{
                xaxis: 'name',
                data: [{dataKey: 'adherence', label: 'RVP TAT %', fill: '#0d9488', unit: '%'}],
              }}
            />
          ) : (
            <EmptyChart>No data available</EmptyChart>
          )}
        </ChartCard>
      </Box>

      {/* Filters */}
      <FilterBar>
        <Stack direction='row' spacing={1.5} alignItems='center'>
          <Autocomplete
            sx={{width: 250}}
            size='small'
            onChange={(_event, value) => setSelectedCourierNames(value?.value || null)}
            options={courierNameOptions}
            getOptionLabel={(option) => option.label}
            renderInput={(params) => <TextField {...params} label='Search Service Type' size='small' />}
          />
          <FormControl size='small' sx={{minWidth: 130}}>
            <InputLabel>Volume</InputLabel>
            <Select value={volumeRange} label='Volume' onChange={(e) => setVolumeRange(e.target.value)}>
              {volumeRangeOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
        <PerformanceLegend />
      </FilterBar>

      {/* Table */}
      <MaterialTable
        rows={sortedData}
        columns={columns}
        exportExcel={false}
        reportName='REVERSE_PERFORMANCE'
        size='medium'
        itemsPerPage={100}
        frozenColumnsCount={2}
        minWidth={1000}
        noDataMessage='No reverse courier performance data available'
      />
    </Box>
  )
}
